import { Injectable, signal, WritableSignal } from '@angular/core';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';

import * as NotificationsActions from '../state/actions';
import * as notificationsSelectors from '../state/selectors/notifications.selectors';
import { NotificationsState } from '../state/reducers/notifications.reducer';
import { Notification, NotificationPreferenceResponse, NotificationPreferenceUpdate } from '../models/Notifications';
import { NotificationQueryParams } from '../services/notification.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationsFacade {
  notifications$;
  nextPageUrl$;
  prevPageUrl$;
  totalCount$;
  preferences$;

  notificationsSignal: WritableSignal<Notification[]> = signal<Notification[]>([]);
  nextPageUrlSignal = signal<string | null>(null);
  prevPageUrlSignal = signal<string | null>(null);
  totalCountSignal = signal<number>(0);
  preferencesSignal = signal<NotificationPreferenceResponse[]>([]);

  constructor(private store: Store<NotificationsState>) {
    this.notifications$ = this.store.select(notificationsSelectors.getNotificationList);
    this.nextPageUrl$ = this.store.select(notificationsSelectors.getNextPageUrl);
    this.prevPageUrl$ = this.store.select(notificationsSelectors.getPreviousPageUrl);
    this.totalCount$ = this.store.select(notificationsSelectors.getTotalCount);
    this.preferences$ = this.store.select(notificationsSelectors.getNotificationPreferences);

    this.notificationsSignal = toSignal(this.notifications$, { initialValue: [] }) as WritableSignal<Notification[]>;
    this.nextPageUrlSignal = toSignal(this.nextPageUrl$, { initialValue: null }) as WritableSignal<string | null>;
    this.prevPageUrlSignal = toSignal(this.prevPageUrl$, { initialValue: null }) as WritableSignal<string | null>;
    this.totalCountSignal = toSignal(this.totalCount$, { initialValue: 0 }) as WritableSignal<number>;
    this.preferencesSignal = toSignal(this.preferences$, { initialValue: [] }) as WritableSignal<
      NotificationPreferenceResponse[]
    >;
  }

  public getNotifications(params?: NotificationQueryParams) {
    this.store.dispatch(NotificationsActions.NotificationsUiActions.GetNotifications({ params }));
  }

  public markNotificationsRead(ids: number[]) {
    this.store.dispatch(NotificationsActions.NotificationsUiActions.MarkNotificationsRead({ ids }));
  }

  public getNotificationPreferences() {
    this.store.dispatch(NotificationsActions.NotificationsUiActions.GetNotificationPreferences());
  }

  public updateNotificationPreferences(preferences: NotificationPreferenceUpdate[]) {
    this.store.dispatch(NotificationsActions.NotificationsUiActions.UpdateNotificationPreferences({ preferences }));
  }
}
